/**
 * PurchaseHistoryPage - Buyer's purchase history and order tracking
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { 
  ShoppingBag, Package, Truck, CheckCircle, Clock,
  Star, MessageSquare, BookOpen, Loader2, XCircle,
  RefreshCw, Eye
} from 'lucide-react';
import { toast } from 'sonner';
import { useCountry } from '../context/CountryContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Status badge colors
const STATUS_COLORS = {
  pending: 'bg-yellow-100 text-yellow-800',
  confirmed: 'bg-blue-100 text-blue-800',
  shipped: 'bg-purple-100 text-purple-800',
  delivered: 'bg-cyan-100 text-cyan-800',
  completed: 'bg-green-100 text-green-800',
  cancelled: 'bg-red-100 text-red-800'
};

// Order Card Component
const OrderCard = ({ order, currencySymbol, onReview, onViewDetails }) => {
  const statusColor = STATUS_COLORS[order.status] || 'bg-gray-100 text-gray-800';
  
  return (
    <Card className="hover:shadow-md transition-all" data-testid={`order-card-${order.id}`}>
      <CardContent className="p-4">
        <div className="flex items-start gap-4">
          {/* Image */}
          <div className="w-20 h-20 bg-gray-100 rounded-lg overflow-hidden shrink-0">
            {order.listing_image ? (
              <img src={order.listing_image} alt={order.listing_title} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <BookOpen className="w-8 h-8 text-gray-300" />
              </div>
            )}
          </div>
          
          {/* Details */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div>
                <h4 className="font-semibold line-clamp-1">{order.listing_title}</h4>
                <p className="text-sm text-gray-500">Order #{order.id?.slice(-8)}</p>
              </div>
              <Badge className={statusColor}>
                {order.status?.charAt(0).toUpperCase() + order.status?.slice(1)}
              </Badge>
            </div>
            
            <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
              <span className="font-semibold text-lg text-gray-900">
                {currencySymbol}{order.amount?.toLocaleString()}
              </span>
              <span>
                {new Date(order.created_at).toLocaleDateString()}
              </span>
            </div>
            
            <div className="flex gap-2 mt-3">
              <Button size="sm" variant="outline" onClick={() => onViewDetails(order)}>
                <Eye className="w-3 h-3 mr-1" />
                Details
              </Button>
              {order.status === 'completed' && !order.reviewed && (
                <Button size="sm" onClick={() => onReview(order)}>
                  <Star className="w-3 h-3 mr-1" />
                  Leave Review
                </Button>
              )}
              {order.status === 'shipped' && (
                <Button size="sm" variant="outline">
                  <Truck className="w-3 h-3 mr-1" />
                  Track
                </Button>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Star Rating Component
const StarRating = ({ rating, setRating }) => (
  <div className="flex gap-1">
    {[1, 2, 3, 4, 5].map(star => (
      <button
        key={star}
        type="button"
        onClick={() => setRating(star)}
        className="focus:outline-none"
      >
        <Star 
          className={`w-8 h-8 transition-colors ${
            star <= rating ? 'text-amber-400 fill-amber-400' : 'text-gray-300'
          }`}
        />
      </button>
    ))}
  </div>
);

export default function PurchaseHistoryPage() {
  const navigate = useNavigate();
  const { country } = useCountry();
  
  const [email, setEmail] = useState('');
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewTitle, setReviewTitle] = useState('');
  const [reviewComment, setReviewComment] = useState('');
  
  const currencySymbol = country === 'UK' ? '£' : 'Rs.';
  
  const fetchOrders = async () => {
    if (!email) {
      toast.error('Please enter your email');
      return;
    }
    
    setLoading(true);
    setSearched(true);
    
    try {
      const response = await fetch(`${API_URL}/api/books-media/orders/buyer/${email}?country=${country}`);
      const data = await response.json();
      setOrders(data.orders || []);
    } catch (err) {
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };
  
  const handleReview = (order) => {
    setSelectedOrder(order);
    setReviewRating(5);
    setReviewTitle('');
    setReviewComment('');
    setShowReviewModal(true);
  };
  
  const handleViewDetails = (order) => {
    setSelectedOrder(order);
    setShowDetailsModal(true);
  };
  
  const submitReview = async () => {
    if (!reviewRating) {
      toast.error('Please select a rating');
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/books-media/reviews?country=${country}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          order_id: selectedOrder.id,
          listing_id: selectedOrder.listing_id,
          reviewer_email: email,
          reviewer_name: email.split('@')[0],
          seller_email: selectedOrder.seller_email,
          rating: reviewRating,
          title: reviewTitle,
          comment: reviewComment
        })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to submit review');
      }
      
      toast.success('Review submitted!');
      setShowReviewModal(false);
      
      // Mark order as reviewed
      setOrders(orders.map(o => 
        o.id === selectedOrder.id ? { ...o, reviewed: true } : o
      ));
    } catch (err) {
      toast.error(err.message);
    }
  };
  
  // Filter orders by status
  const filteredOrders = activeTab === 'all' 
    ? orders 
    : orders.filter(o => o.status === activeTab);
  
  // Count by status
  const statusCounts = {
    all: orders.length,
    pending: orders.filter(o => o.status === 'pending').length,
    shipped: orders.filter(o => o.status === 'shipped').length,
    completed: orders.filter(o => o.status === 'completed').length
  };
  
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 text-white py-12">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center gap-4 mb-6">
            <div className="p-3 bg-white/20 rounded-xl">
              <ShoppingBag className="w-8 h-8" />
            </div>
            <div>
              <h1 className="text-3xl font-bold">Purchase History</h1>
              <p className="text-white/80">Track your orders and leave reviews</p>
            </div>
          </div>
          
          {/* Email Input */}
          <div className="flex gap-3 max-w-md">
            <Input
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="bg-white text-gray-900"
              onKeyDown={(e) => e.key === 'Enter' && fetchOrders()}
            />
            <Button onClick={fetchOrders} className="bg-white text-teal-600 hover:bg-gray-100">
              <RefreshCw className="w-4 h-4 mr-2" />
              Load Orders
            </Button>
          </div>
        </div>
      </div>
      
      <div className="max-w-5xl mx-auto px-4 py-8">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-teal-500" />
          </div>
        ) : !searched ? (
          <Card className="text-center py-16">
            <CardContent>
              <ShoppingBag className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <h2 className="text-xl font-semibold mb-2">Enter Your Email</h2>
              <p className="text-gray-500">Enter your email above to view your purchase history</p>
            </CardContent>
          </Card>
        ) : orders.length === 0 ? (
          <Card className="text-center py-16">
            <CardContent>
              <Package className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <h2 className="text-xl font-semibold mb-2">No Orders Yet</h2>
              <p className="text-gray-500 mb-6">Start shopping to see your orders here!</p>
              <Button onClick={() => navigate('/books-media')}>
                <BookOpen className="w-4 h-4 mr-2" />
                Browse Marketplace
              </Button>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="mb-6">
                <TabsTrigger value="all">
                  All Orders
                  <Badge variant="secondary" className="ml-2">{statusCounts.all}</Badge>
                </TabsTrigger>
                <TabsTrigger value="pending">
                  Pending
                  {statusCounts.pending > 0 && (
                    <Badge variant="secondary" className="ml-2">{statusCounts.pending}</Badge>
                  )}
                </TabsTrigger>
                <TabsTrigger value="shipped">
                  Shipped
                  {statusCounts.shipped > 0 && (
                    <Badge variant="secondary" className="ml-2">{statusCounts.shipped}</Badge>
                  )}
                </TabsTrigger>
                <TabsTrigger value="completed">
                  Completed
                  {statusCounts.completed > 0 && (
                    <Badge variant="secondary" className="ml-2">{statusCounts.completed}</Badge>
                  )}
                </TabsTrigger>
              </TabsList>
              
              <TabsContent value={activeTab}>
                <div className="space-y-4">
                  {filteredOrders.map(order => (
                    <OrderCard
                      key={order.id}
                      order={order}
                      currencySymbol={currencySymbol}
                      onReview={handleReview}
                      onViewDetails={handleViewDetails}
                    />
                  ))}
                </div>
              </TabsContent>
            </Tabs>
          </>
        )}
      </div>
      
      {/* Review Modal */}
      <Dialog open={showReviewModal} onOpenChange={setShowReviewModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Leave a Review</DialogTitle>
            <DialogDescription>
              Share your experience with this purchase
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="font-medium">{selectedOrder?.listing_title}</p>
              <p className="text-sm text-gray-500">Order #{selectedOrder?.id?.slice(-8)}</p>
            </div>
            
            <div>
              <Label className="mb-2 block">Your Rating</Label>
              <StarRating rating={reviewRating} setRating={setReviewRating} />
            </div>
            
            <div>
              <Label>Review Title (optional)</Label>
              <Input
                placeholder="e.g., Great condition, fast shipping!"
                value={reviewTitle}
                onChange={(e) => setReviewTitle(e.target.value)}
              />
            </div>
            
            <div>
              <Label>Your Review (optional)</Label>
              <Textarea
                placeholder="Share more details about your experience..."
                value={reviewComment}
                onChange={(e) => setReviewComment(e.target.value)}
                rows={4}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReviewModal(false)}>Cancel</Button>
            <Button onClick={submitReview}>
              <Star className="w-4 h-4 mr-2" />
              Submit Review
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Order Details Modal */}
      <Dialog open={showDetailsModal} onOpenChange={setShowDetailsModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Order Details</DialogTitle>
            <DialogDescription>
              Order #{selectedOrder?.id?.slice(-8)}
            </DialogDescription>
          </DialogHeader>
          
          {selectedOrder && (
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-gray-100 rounded-lg overflow-hidden">
                  {selectedOrder.listing_image ? (
                    <img src={selectedOrder.listing_image} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <BookOpen className="w-6 h-6 text-gray-300" />
                    </div>
                  )}
                </div>
                <div>
                  <h4 className="font-semibold">{selectedOrder.listing_title}</h4>
                  <p className="text-2xl font-bold">{currencySymbol}{selectedOrder.amount?.toLocaleString()}</p>
                </div>
              </div>
              
              <div className="border-t pt-4 space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-500">Status</span>
                  <Badge className={STATUS_COLORS[selectedOrder.status]}>
                    {selectedOrder.status?.charAt(0).toUpperCase() + selectedOrder.status?.slice(1)}
                  </Badge>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Order Date</span>
                  <span>{new Date(selectedOrder.created_at).toLocaleDateString()}</span>
                </div>
                {selectedOrder.shipping_address && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Shipping To</span>
                    <span className="text-right max-w-[200px]">{selectedOrder.shipping_address}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-500">Seller</span>
                  <span>{selectedOrder.seller_email}</span>
                </div>
              </div>
              
              {/* Status Timeline */}
              {selectedOrder.status_history && selectedOrder.status_history.length > 0 && (
                <div className="border-t pt-4">
                  <h5 className="font-medium mb-3">Order Timeline</h5>
                  <div className="space-y-2">
                    {selectedOrder.status_history.map((entry, i) => (
                      <div key={i} className="flex items-center gap-3 text-sm">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        <span className="capitalize">{entry.status}</span>
                        <span className="text-gray-400 text-xs">
                          {new Date(entry.timestamp).toLocaleString()}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDetailsModal(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
